/**
 * What a shift's paid time earns: the statutory floor on the rate, the
 * minimum-shift guarantee, the premium buckets and the money.
 *
 * Payroll applies this to hours worked and the profitability forecast to hours
 * rostered. A shift flagged before publication has to be paid the same way
 * afterwards, so the rules live here once rather than in each caller.
 */

import { calculateMinuteBasedPay, type OvertimeAllocation } from './rules';
import {
  IRISH_SECURITY_ERO_MINIMUM_SHIFT_MINUTES,
  IRISH_SECURITY_ERO_UNSOCIAL_END_MINUTE,
  IRISH_SECURITY_ERO_UNSOCIAL_MIN_MINUTES,
  IRISH_SECURITY_ERO_UNSOCIAL_START_MINUTE,
  isIrishCountryCode,
  securityEroLatestHourlyRateChange,
  securityEroMinimumHourlyRate,
  securityEroUnsocialPremium,
  type EffectiveRateChange,
} from './securityEro';
import {
  intervalMinutes,
  roundMoney,
  sumMinutesForLocalDays,
  sumMinutesForLocalWindow,
  type TimeInterval,
} from './time';
import type { PayrollSettings, ResolvedRate } from './types';

/** The company settings the premium rules read. */
export type PremiumSettings = Pick<
  PayrollSettings,
  | 'applyMinimumShiftPay'
  | 'useSecurityEroUnsocialRates'
  | 'unsocialHoursStartMinute'
  | 'unsocialHoursEndMinute'
  | 'unsocialHoursMinMinutes'
>;

/** The lowest lawful hourly rate for work in a country on a date, where one is known. */
export function statutoryMinimumHourlyRate(countryCode: string, dateKey: string): number | null {
  return isIrishCountryCode(countryCode) ? securityEroMinimumHourlyRate(dateKey) : null;
}

/** The last time that minimum moved, on or before a date. */
export function latestStatutoryRateChange(countryCode: string, dateKey: string): EffectiveRateChange | null {
  return isIrishCountryCode(countryCode) ? securityEroLatestHourlyRateChange(dateKey) : null;
}

export interface FlooredRate {
  rate: ResolvedRate;
  /** The rate before the floor, for saying what was lifted. */
  resolvedHourlyRate: number;
  floorApplied: boolean;
}

/** A pay band or company rate below the statutory minimum is lifted to it. */
export function applyStatutoryRateFloor(
  resolvedRate: ResolvedRate,
  countryCode: string,
  dateKey: string,
): FlooredRate {
  const minimum = statutoryMinimumHourlyRate(countryCode, dateKey);
  const floorApplied = minimum !== null && resolvedRate.baseHourlyRate < minimum;

  return {
    rate: floorApplied ? { ...resolvedRate, baseHourlyRate: minimum as number } : resolvedRate,
    resolvedHourlyRate: resolvedRate.baseHourlyRate,
    floorApplied,
  };
}

/**
 * Statutory unsocial-hours premium by shift date. The ERO schedule stepped up
 * over time, so a historic export must use the rate in force back then.
 */
export function resolveUnsocialPremium(
  settings: PremiumSettings,
  rate: ResolvedRate,
  dateKey: string,
  countryCode: string,
): number {
  if (isIrishCountryCode(countryCode)) {
    return securityEroUnsocialPremium(dateKey) ?? rate.unsocialShiftPremium;
  }
  if (!settings.useSecurityEroUnsocialRates) return rate.unsocialShiftPremium;

  return securityEroUnsocialPremium(dateKey) ?? rate.unsocialShiftPremium;
}

export interface PaidTimeContext {
  timeZone: string;
  countryCode: string;
  /** After the statutory floor; only its minimum-shift length is read here. */
  rate: ResolvedRate;
  settings: PremiumSettings;
  isPublicHoliday: (dateKey: string) => boolean;
}

export interface PaidTime {
  /** Minutes worked and paid, before any minimum-shift guarantee. */
  payableMinutes: number;
  /** Unworked minutes paid to bring a short shift up to the guarantee. */
  minimumTopUpMinutes: number;
  paidMinutes: number;
  sundayMinutes: number;
  publicHolidayMinutes: number;
  unsocialMinutes: number;
  unsocialQualified: boolean;
}

/**
 * Sort paid time into what attracts a premium. `workedIntervals` is the time
 * paid for: unpaid breaks are already taken out.
 *
 * An Irish shift takes the ERO's window, threshold and four-hour guarantee
 * whatever the company's settings say; they can raise an entitlement, never
 * lower it.
 */
export function classifyPaidTime(workedIntervals: TimeInterval[], context: PaidTimeContext): PaidTime {
  const { timeZone, countryCode, rate, settings } = context;
  const payableMinutes = Math.round(intervalMinutes(workedIntervals));

  const isIrishEroShift = isIrishCountryCode(countryCode);
  const minimumThreshold = isIrishEroShift
    ? Math.max(rate.minimumShiftPayMinutes, IRISH_SECURITY_ERO_MINIMUM_SHIFT_MINUTES)
    : rate.minimumShiftPayMinutes;
  const appliesMinimumShiftPay = isIrishEroShift || settings.applyMinimumShiftPay;
  const minimumTopUpMinutes = appliesMinimumShiftPay && payableMinutes > 0 && payableMinutes < minimumThreshold
    ? minimumThreshold - payableMinutes
    : 0;

  const sundayMinutes = sumMinutesForLocalDays(
    workedIntervals,
    timeZone,
    (_dateKey, weekdayIndex) => weekdayIndex === 0,
  );
  const publicHolidayMinutes = sumMinutesForLocalDays(
    workedIntervals,
    timeZone,
    (key) => context.isPublicHoliday(key),
  );
  const unsocialMinutes = sumMinutesForLocalWindow(
    workedIntervals,
    timeZone,
    isIrishEroShift ? IRISH_SECURITY_ERO_UNSOCIAL_START_MINUTE : settings.unsocialHoursStartMinute,
    isIrishEroShift ? IRISH_SECURITY_ERO_UNSOCIAL_END_MINUTE : settings.unsocialHoursEndMinute,
  );
  const unsocialMinMinutes = isIrishEroShift
    ? IRISH_SECURITY_ERO_UNSOCIAL_MIN_MINUTES
    : settings.unsocialHoursMinMinutes;

  return {
    payableMinutes,
    minimumTopUpMinutes,
    paidMinutes: payableMinutes + minimumTopUpMinutes,
    sundayMinutes,
    publicHolidayMinutes,
    unsocialMinutes,
    unsocialQualified: unsocialMinutes >= unsocialMinMinutes,
  };
}

export interface ShiftPay {
  /** Ordinary minutes including the minimum-shift top-up, which is paid at the plain rate. */
  ordinaryMinutes: number;
  ordinaryPay: number;
  overtimePay: number;
  doubleTimePay: number;
  sundayPremiumPay: number;
  publicHolidayPremiumPay: number;
  unsocialPremiumPay: number;
  estimatedBasePay: number;
  estimatedTotalPay: number;
}

/** Turn a shift's minutes into money, once overtime has been allocated across the week. */
export function priceShift(
  paidTime: Pick<PaidTime, 'minimumTopUpMinutes' | 'sundayMinutes' | 'publicHolidayMinutes' | 'unsocialQualified'>,
  allocation: OvertimeAllocation,
  rate: ResolvedRate,
  unsocialShiftPremium: number,
): ShiftPay {
  const ordinaryMinutes = allocation.ordinaryMinutes + paidTime.minimumTopUpMinutes;
  const {
    ordinaryPay,
    overtimePay,
    doubleTimePay,
    sundayPremiumPay,
    publicHolidayPremiumPay,
  } = calculateMinuteBasedPay({
    ordinaryMinutes,
    dailyOvertimeMinutes: allocation.dailyOvertimeMinutes,
    weeklyOvertimeMinutes: allocation.weeklyOvertimeMinutes,
    doubleTimeMinutes: allocation.doubleTimeMinutes,
    sundayMinutes: paidTime.sundayMinutes,
    publicHolidayMinutes: paidTime.publicHolidayMinutes,
  }, {
    baseHourlyRate: rate.baseHourlyRate,
    overtimeDailyMultiplier: rate.overtimeDailyMultiplier,
    overtimeWeeklyMultiplier: rate.overtimeWeeklyMultiplier,
    doubleTimeMultiplier: rate.doubleTimeMultiplier,
    sundayPremiumMultiplier: rate.sundayPremiumMultiplier,
    publicHolidayMultiplier: rate.publicHolidayMultiplier,
  });
  const unsocialPremiumPay = roundMoney(paidTime.unsocialQualified ? unsocialShiftPremium : 0);

  const estimatedBasePay = roundMoney(ordinaryPay + overtimePay + doubleTimePay);
  const estimatedTotalPay = roundMoney(
    estimatedBasePay + sundayPremiumPay + publicHolidayPremiumPay + unsocialPremiumPay,
  );

  return {
    ordinaryMinutes,
    ordinaryPay,
    overtimePay,
    doubleTimePay,
    sundayPremiumPay,
    publicHolidayPremiumPay,
    unsocialPremiumPay,
    estimatedBasePay,
    estimatedTotalPay,
  };
}
